import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { MatBadge } from '@angular/material/badge'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatTab, MatTabContent, MatTabGroup } from '@angular/material/tabs'
import {
  ElderBasicPaneLayoutComponent,
  ElderPaneComponent,
  ElderTabGroupRoutingDirective,
  ElderToastService,
  ElderToolbarColumnDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { FacilityContextMenuComponent } from '../../../context/facility-context/facility-context-menu/facility-context-menu.component'
import { FacilityContextDirective } from '../../../context/facility-context/facility-context.directive'
import { Facility } from '../../../facilities/facility'
import { GroupedEdge } from '../grouped-edge'
import { GroupedEdgeTarget } from '../grouped-edge-target'
import { ShopfloorEditService } from '../shopfloor-edit.service'
import { ShopfloorLayoutContext } from '../shopfloor-layout-context'
import { ShopfloorLayoutContextService } from '../shopfloor-layout-context.service'
import { ShopfloorLayoutEditorComponent } from '../shopfloor-layout-editor/shopfloor-layout-editor.component'
import { ShopfloorLayoutGraphComponent } from '../shopfloor-layout-graph/shopfloor-layout-graph.component'
import { ShopfloorLayoutSpecEdge } from '../shopfloor-layout-spec-edge'
import { ShopfloorLayoutSpecNode } from '../shopfloor-layout-spec-node'
import { ShopfloorLayoutSpec } from '../spec/shopfloor-layout-spec'
import { ShopfloorLayoutSpecService } from '../spec/shopfloor-layout-spec.service'

@Component({
  selector: 'app-shopfloor-layout',
  templateUrl: './shopfloor-layout.component.html',
  styleUrls: ['./shopfloor-layout.component.scss'],
  imports: [
    ElderToolbarColumnDirective,
    FacilityContextMenuComponent,
    FacilityContextDirective,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    MatTabContent,
    ShopfloorLayoutGraphComponent,
    ShopfloorLayoutEditorComponent,
    MatIconButton,
    MatBadge,
    MatIcon,
    AsyncPipe,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
  ],
})
export class ShopfloorLayoutComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  public readonly context$ = new BehaviorSubject<ShopfloorLayoutContext>(
    ShopfloorLayoutContext.EMPTY
  )
  public readonly spec$ = new BehaviorSubject<ShopfloorLayoutSpec>(ShopfloorLayoutSpec.EMPTY)
  public readonly pendingChanges$ = new BehaviorSubject<boolean>(false)
  private _facility: Facility
  private layoutSpecService = inject(ShopfloorLayoutSpecService)
  private contextService = inject(ShopfloorLayoutContextService)
  private editService = inject(ShopfloorEditService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set facility(facility: Facility) {
    this._facility = facility
    this.loadFacilityLayout()
  }

  public set spec(spec: ShopfloorLayoutSpec) {
    this.spec$.next(spec)
  }

  public get spec(): ShopfloorLayoutSpec {
    return this.spec$.getValue()
  }

  public get context(): ShopfloorLayoutContext {
    return this.context$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public Methods                                                          *
   *                                                                         *
   **************************************************************************/

  public onSpecChange(newSpec: ShopfloorLayoutSpec) {
    this.spec = newSpec
    this.pendingChanges$.next(true)
  }

  public save() {
    this.layoutSpecService.update(this.spec).subscribe({
      next: (updatedSpec) => {
        this.spec = updatedSpec
        this.pendingChanges$.next(false)
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  public onNodeClicked(node: ShopfloorLayoutSpecNode) {
    const relevantEdges = this.spec.edges.filter((e) => e.source === node.id)
    const groupedEdgeTargets = relevantEdges.map((te) => this.buildTarget(te))
    this.editService
      .openNodeEditDialog(
        this.context,
        this.spec.id,
        this.spec.facility,
        this.editService.buildGroupedEdges(this.spec, this.context),
        new GroupedEdge(node, groupedEdgeTargets)
      )
      .subscribe((newSpec: ShopfloorLayoutSpec) => {
        this.onSpecChange(newSpec)
      })
  }

  public onEdgeClicked(edge: ShopfloorLayoutSpecEdge) {
    this.editService
      .openEdgeEditDialog(this.context, this.spec, edge)
      .subscribe((newSpec: ShopfloorLayoutSpec) => {
        this.onSpecChange(newSpec)
      })
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private loadFacilityLayout(): void {
    this.layoutSpecService.findById(this._facility.id).subscribe({
      next: (spec) => this.spec$.next(spec),
      error: (err) => {
        this.log.error('Failed to load spec', err)
        const spec = ShopfloorLayoutSpec.EMPTY
        spec.facility = this._facility.id
        this.spec$.next(spec)
      },
    })

    this.contextService.findById(this._facility.id).subscribe({
      next: (context) => this.context$.next(context),
      error: (err) => {
        this.log.error('Failed to load context', err)
        this.context$.next(ShopfloorLayoutContext.EMPTY)
      },
    })
  }

  private buildTarget(te: ShopfloorLayoutSpecEdge) {
    const node = this.context.nodes.find((n) => n.id === te.target)
    return new GroupedEdgeTarget(node.id, node, te.transportCost)
  }
}
